import { readonly, ref } from "@nuxtjs/composition-api";
import { Logger } from "~/helpers/logger";
import type { GetProductSearchParams } from "~/modules/catalog/product/types";
import useApi from "~/composables/useApi";
import { sortingOptions } from "~/modules/catalog/category/composables/useFacet/sortingOptions";
import { perPageOptions } from "~/modules/catalog/category/composables/useFacet/perPageOptions";
import { createProductAttributeFilterInput } from "~/modules/catalog/category/composables/useFacet/input/createProductAttributeFilterInput";
import { createProductAttributeSortInput } from "~/modules/catalog/category/composables/useFacet/input/createProductAttributeSortInput";
import { Products } from "~/modules/GraphQL/types";
import getFacetDataQuery from "./getFacetData.gql";
import getProductSearchGql from "~/modules/catalog/category/composables/queries/getProductSearch.gql";
import type { UseFacetInterface, UseFacetErrors, UseFacetSearchResult, FacetSearchParams } from "./useFacet";
import { fetchWithRetry } from "~/helpers";
import { GET_PRODUCT_SEARCH , GET_PRODUCT_SEARCH_WITH_AGGREGATIONS} from "~/modules/catalog/product/queries/getProduct.gql";

/**
 * Allows searching for products with pagination, totals and sorting options.
 *
 * See the {@link UseFacetInterface} for a list of methods and values available in this composable.
 */
export function useFacet(): UseFacetInterface {
  const { query } = useApi();
  const loading = ref(false);
  const result = ref<UseFacetSearchResult>({ data: null, input: null });
  const resultAgg = ref<UseFacetSearchResult>({ data: null, input: null });
  const error = ref<UseFacetErrors>({
    search: null,
    searchAgg: null,
  });
  const defaultItemsPerPage = 20;
  const search = async (params?: FacetSearchParams) => {
    Logger.debug("useFacet/search", params);

    result.value.input = params;
    try {
      loading.value = true;

      const pageSize = params.itemsPerPage ? params.itemsPerPage : defaultItemsPerPage;

      const productSearchParams: GetProductSearchParams = {
        pageSize,
        search: params.term ? params.term : "",
        filter: createProductAttributeFilterInput(params),
        sort: createProductAttributeSortInput(params.sort || ""),
        currentPage: params.page,
      };

      try {
        const { data, errors } = await fetchWithRetry(() => query<{ productSearch: Products }>(GET_PRODUCT_SEARCH, productSearchParams));
        if (errors) {
          console.error("GraphQL errors:", errors);
        }
        const products = data?.productSearch ?? null;
        Logger.debug("[Result]:", { products });

        result.value.data = {
          items: products?.items ?? [],
          total: products?.total_count,
          aggregations: products?.aggregations ?? [],
          availableSortingOptions: sortingOptions,
          perPageOptions,
          itemsPerPage: pageSize,
        };
        error.value.search = null;
      } catch (networkErr) {
        console.error("Network error during query:", networkErr);
        throw networkErr; // Re-throw để được bắt bởi catch bên ngoài
      }
    } catch (err) {
      error.value.search = err;
      Logger.error("useFacet/search", err);
      console.error("Error in search:", err); // Thêm log chi tiết hơn
    } finally {
      loading.value = false;
    }
  };

  const searchAgg = async (params?: FacetSearchParams) => {
    Logger.debug("useFacet/searchAgg", params);

    resultAgg.value.input = params;
    try {
      loading.value = true;

      const pageSize = params.itemsPerPage ? params.itemsPerPage : defaultItemsPerPage;

      const productSearchParams: GetProductSearchParams = {
        pageSize,
        search: params.term ? params.term : "",
        filter: createProductAttributeFilterInput(params),
        sort: createProductAttributeSortInput(params.sort || ""),
        currentPage: params.page,
      };

      try {
        const { data, errors } = await fetchWithRetry(() => query<{ productSearch: Products }>(GET_PRODUCT_SEARCH_WITH_AGGREGATIONS, productSearchParams));
        if (errors) {
          console.error("GraphQL errors:", errors);
        }
        const products = data?.productSearch ?? null;
        Logger.debug("[Result]:", { products });

        resultAgg.value.data = {
          items: products?.items ?? [],
          total: products?.total_count,
          aggregations: products?.aggregations ?? [],
          availableSortingOptions: sortingOptions,
          perPageOptions,
          itemsPerPage: pageSize,
        };
        error.value.search = null;
      } catch (networkErr) {
        console.error("Network error during query:", networkErr);
        throw networkErr; 
      }
    } catch (err) {
      error.value.search = err;
      Logger.error("useFacet/search", err);
      console.error("Error in search:", err); // Thêm log chi tiết hơn
    } finally {
      loading.value = false;
    }
  };

  return {
    search,
    searchAgg,
    result,
    resultAgg,
    error: readonly(error),
    loading: readonly(loading),
  };
}

export * from "./useFacet";
export default useFacet;
